import { checkUser } from "./apiService";
import { initializeUser, UserDto } from "./dto/user.dto";
import { fetchFormConfig } from "./form";
import { getCurrentLanguage } from "./i18n/i18n";
import { showTimeoutMessage } from "./notification";
import {
  getCompanyId,
  getInactivityTimeout,
  getNotificationCountdownInterval,
  getTotalSteps,
  resetUserAdditionalData,
  setInactivityTimeout,
  setNotificationCountdownInterval,
  setUser,
  setUserProperty,
  TIMEOUT_DURATION,
} from "./store";
import { resetFormFields, updateProgressBar } from "./uiFunction";
import { validateInput } from "./validation";

// Reset to landing page
export function resetToLanding(): void {
  clearTimeout(getInactivityTimeout());

  const thankYou = document.getElementById("thankYou") as HTMLElement;
  thankYou.style.opacity = "0";
  thankYou.style.pointerEvents = "none";

  const landing = document.getElementById("landing") as HTMLElement;
  landing.style.opacity = "1";
  landing.style.pointerEvents = "auto";
  landing.style.display = "flex";

  const formContainer = document.querySelector(".bg-white") as HTMLElement;
  formContainer.style.opacity = "1";
  formContainer.style.pointerEvents = "auto";

  const signupForm = document.getElementById("signupForm") as HTMLFormElement;
  signupForm.reset();
  setUser(initializeUser());

  updateProgressBar(1);
  fetchFormConfig();
  resetFormFields();

  document.querySelectorAll(".slide").forEach((slide, index) => {
    if (index === 0) {
      slide.classList.remove("slide-hidden");
    } else {
      slide.classList.add("slide-hidden");
    }
  });
}

// Reset inactivity timeout
export function resetInactivityTimer(): void {
   const landing = document.getElementById("landing") as HTMLElement;
  if (landing && landing.style.opacity !== "0") {
    // Qui puoi anche stoppare i timer se vuoi essere sicuro che siano sempre fermi in "display"
    if (getInactivityTimeout()) clearTimeout(getInactivityTimeout());
    const existingNotification = document.getElementById("notificationTimeout");
    if (existingNotification) {
      existingNotification.remove();
      if (getNotificationCountdownInterval()) {
        clearInterval(getNotificationCountdownInterval()!);
        setNotificationCountdownInterval(null);
      }
    }
    return;
  }
  if (getInactivityTimeout()) clearTimeout(getInactivityTimeout());

  const existingNotification = document.getElementById("notificationTimeout");
  if (existingNotification) {
    existingNotification.remove();
    if (getNotificationCountdownInterval()) {
      clearInterval(getNotificationCountdownInterval()!);
      setNotificationCountdownInterval(null);
    }
  }

  setInactivityTimeout(
    setTimeout(() => {
      showTimeoutMessage(getCurrentLanguage());
    }, TIMEOUT_DURATION)
  );
}

// Show a thank you message
export function showThankYou(): void {
  resetInactivityTimer();
  const formContainer = document.querySelector(".bg-white") as HTMLElement;
  formContainer.style.opacity = "0";
  formContainer.style.pointerEvents = "none";

  const thankYou = document.getElementById("thankYou") as HTMLElement;
  thankYou.style.opacity = "1";
  thankYou.style.pointerEvents = "auto";

  let timeLeft = 30;
  const timerElement = document.getElementById("timer") as HTMLElement;

  const countdown = setInterval(() => {
    timeLeft--;
    timerElement.textContent = timeLeft.toString();

    if (timeLeft <= 0) {
      clearInterval(countdown);
      resetToLanding();
    }
  }, 1000);
}

// Navigate to the next slide
export async function nextSlide(currentSlideNumber: number): Promise<void> {
  const currentSlideElement = document.getElementById(
    `slide${currentSlideNumber}`
  ) as HTMLElement;

  const nextStep = getTotalSteps() === 3 ? currentSlideNumber + 1 : 3;

  const nextSlideElement = document.getElementById(
    `slide${nextStep}`
  ) as HTMLElement;

  const isValid = await validateSlide(currentSlideNumber);

  if (!isValid) return;
  currentSlideElement.classList.add("slide-hidden");
  nextSlideElement.classList.remove("slide-hidden");
  updateProgressBar(currentSlideNumber + 1);
}

export function previousSlide(currentSlideNumber: number): void {
  const currentSlideElement = document.getElementById(
    `slide${currentSlideNumber}`
  ) as HTMLElement | null;
  const nextStep = getTotalSteps() === 3 ? currentSlideNumber - 1 : 1;

  const previousSlideElement = document.getElementById(
    `slide${nextStep}`
  ) as HTMLElement | null;

  if (!currentSlideElement || !previousSlideElement) {
    console.error("One or more slide elements could not be found.");
    return;
  }

  currentSlideElement.classList.add("slide-hidden");
  previousSlideElement.classList.remove("slide-hidden");
  updateProgressBar(currentSlideNumber - 1);
}

// Validate fields in a slide
async function validateSlide(slideNumber: number): Promise<boolean> {
  const slideElement = document.getElementById(
    `slide${slideNumber}`
  ) as HTMLElement;
  
  const inputs = slideElement.querySelectorAll("input");
  let isValid = true;

  inputs.forEach((input) => {
    // Usa validateInput per gestire validazione e messaggi di errore
    const inputValid = validateInput(
      input as HTMLInputElement,
      getCurrentLanguage()
    );
    if (!inputValid) {
      isValid = false;
    }
  });

  if(slideNumber === 2) {
    resetUserAdditionalData();
  }

  if(slideNumber === 1) {
    const email = document.getElementById("email") as HTMLInputElement;
    const phone = document.getElementById("phone") as HTMLInputElement;
    const phonePrefix = document.getElementById("prefix") as HTMLSelectElement;
    const isAvailable = await checkUser({ email: email.value, phonePrefix: phonePrefix.value, phoneNumber: phone.value }, getCompanyId(), getCurrentLanguage());
    if(!isAvailable) {
      document.getElementById("errorMessage")?.classList.remove("hidden");
      isValid = false;
    }
  } 

  if (isValid) {
    document.getElementById("errorMessage")?.classList.add("hidden");
    const fieldMap: Record<string, keyof UserDto> = {
      name: "name",
      surname: "surname",
      email: "email",
      phone: "phone",
      address: "address",
      birthdate: "birthdate",
      phonePrefix: "phonePrefix",
    };

    inputs.forEach((input) => {
      const key = fieldMap[input.name];
      if (input.name !== "address") {
        if (key) {
          if(key === "phone") {
            const phonePrefix = document.getElementById("prefix") as HTMLSelectElement;
            setUserProperty("phonePrefix", phonePrefix.value);
            setUserProperty("phoneNumber", input.value);
          } else {
            setUserProperty(key, input.value);
          }
        } else {
          setUserProperty("additionalData", {
            name: input.name,
            value: input.value,
            configFieldId: input.id,
          });
        }
      }
    });
  }
  return isValid;
}
